// @dx utility
// Deterministic display label for a price list.
//
// Priority:
//   1. `list.name` (human override) — returned verbatim
//   2. Auto-composed: `${company_short} · ${product.name} · ${dateRange}`
//
// `dateRange`:
//   - valid_from + valid_to → "MM.YYYY–MM.YYYY"
//   - valid_from only       → "od MM.YYYY"
//   - valid_to only         → "do MM.YYYY"
//   - neither               → "bezterminowo"
//
// Use `{ includeProduct: false }` in contexts where product is already a
// separate column (e.g. list-view rows).
//
// Collision discrimination (when `siblings` provided): if multiple lists
// in the sibling set produce the same base label, the minimum
// disambiguator is appended in order: side → load place → unload place
// → incoterms → amount → id-prefix.

import type { PriceList } from "@shared/types/price_list";

export interface PriceListForFormat
  extends Pick<PriceList, "id" | "name" | "side" | "valid_from" | "valid_to" | "amount" | "currency" | "incoterms"> {
  company?: { short_name?: string | null } | null;
  product?: { name?: string | null } | null;
  load_place?: { short_name?: string | null } | null;
  unload_place?: { short_name?: string | null } | null;
}

export interface FormatPriceListRefOptions {
  /** Include product name in the base label. Default true. */
  includeProduct?: boolean;
  /** Other lists to disambiguate against. If omitted, no discrimination. */
  siblings?: PriceListForFormat[];
}

const MONTH_YEAR = (iso: string | null): string => {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  if (!y || !m) return "";
  return `${m}.${y}`;
};

export const formatPriceListDateRange = (
  validFrom: string | null,
  validTo: string | null,
): string => {
  const from = MONTH_YEAR(validFrom);
  const to = MONTH_YEAR(validTo);
  if (from && to) return `${from}–${to}`;
  if (from) return `od ${from}`;
  if (to) return `do ${to}`;
  return "bezterminowo";
};

const baseLabel = (list: PriceListForFormat, includeProduct: boolean): string => {
  const parts: string[] = [];
  const companyName = list.company?.short_name?.trim();
  if (companyName) parts.push(companyName);
  if (includeProduct) {
    const productName = list.product?.name?.trim();
    if (productName) parts.push(productName);
  }
  parts.push(formatPriceListDateRange(list.valid_from, list.valid_to));
  return parts.join(" · ");
};

const sideLabel = (side: string): string => (side === "load" ? "załadunek" : "rozładunek");

const discriminator = (
  list: PriceListForFormat,
  colliding: PriceListForFormat[],
): string | null => {
  // Try each tier in order; return the first value that makes `list` unique
  // within the colliding set.
  const tiers: Array<(p: PriceListForFormat) => string | null> = [
    (p) => sideLabel(p.side),
    (p) => p.load_place?.short_name ?? null,
    (p) => p.unload_place?.short_name ?? null,
    (p) => p.incoterms ?? null,
    (p) => (p.amount != null ? `${p.amount} ${p.currency}` : null),
    (p) => `#${String(p.id).slice(0, 4)}`,
  ];
  for (const getTierValue of tiers) {
    const mine = getTierValue(list);
    if (mine == null) continue;
    const stillColliding = colliding.some(
      (s) => s.id !== list.id && getTierValue(s) === mine,
    );
    if (!stillColliding) return mine;
  }
  return `#${list.id}`;
};

export function formatPriceListRef(
  list: PriceListForFormat,
  options: FormatPriceListRefOptions = {},
): string {
  if (list.name && list.name.trim() !== "") return list.name.trim();

  const includeProduct = options.includeProduct ?? true;
  const base = baseLabel(list, includeProduct);

  const siblings = options.siblings;
  if (!siblings || siblings.length === 0) return base;

  const colliding = siblings.filter(
    (s) => s.id !== list.id && baseLabel(s, includeProduct) === base,
  );
  if (colliding.length === 0) return base;

  const discr = discriminator(list, [list, ...colliding]);
  return discr ? `${base} · ${discr}` : base;
}
